import type { SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { formatGender } from '../../sessionFieldFormat';

// ─────────────────────────────────────────────────────────────────────────────
// Return-Data catalogue.
//
// The session-detail view (SessionViewScreen) renders a step's stored, normalised
// result in TWO panels, matching the designer layout:
//   • "Details"     (middle) — the substantive identity/account data
//   • "Checks List" (right)  — risk signals + verification metadata
//
// The stored result also carries `session_id`. It is deliberately NOT catalogued: it is
// the internal run id, it is already the row the Overview card shows, and as a Check List
// entry it printed a 36-character UUID that told an admin nothing.
//
// This module is the single source that maps each verification category's canonical
// result keys to a label, a panel, and how to display the value. It mirrors the
// per-step Detail components' field lists (PanResultBlock / FaceDetail / BankDetail
// / …) and the builder's Return-Data groups (Identity/Personal → Details; Risk
// Signals/Metadata → Checks). The stored resultJson is already the normalised,
// admin-filtered object, so we render exactly the keys present.
// ─────────────────────────────────────────────────────────────────────────────

export type FieldDisplay =
  | 'value' // plain text
  | 'score' // numeric → "NN%"
  | 'money' // numeric → "₹N,NN,NNN" (Indian grouping)
  | 'document' // object-storage key (offloaded file) → "Available"
  | 'date' // ISO → locale date-time
  | 'flag' // boolean → OK/bad status icon (okWhen decides which is good)
  | 'list' // array → comma-separated
  | 'status' // verification status string → status icon
  | 'gender'; // provider code (M/F/T) → the word

export interface ReturnField {
  /**
   * Key in the stored result. Supports a dotted path (`esign.document_id`) for
   * results that nest — the verification steps store flat objects, but the document
   * steps group their result under one key.
   */
  key: string;
  label: string;
  panel: 'details' | 'checks';
  display: FieldDisplay;
  /** For `flag` fields: which boolean value is the "good" (green) one. */
  okWhen?: 'no' | 'yes';
}

export interface CategorySpec {
  key: string;
  /** Human label for the category, used in the "<label> Details" subtitle. */
  label: string;
  fields: ReturnField[];
}

const PAN: CategorySpec = {
  key: 'pan',
  label: 'PAN',
  fields: [
    { key: 'pan_holder_name', label: 'PAN holder name', panel: 'details', display: 'value' },
    { key: 'pan_number', label: 'PAN Number', panel: 'details', display: 'value' },
    { key: 'pan_status', label: 'PAN Status', panel: 'details', display: 'value' },
    { key: 'date_of_birth', label: 'Date of Birth', panel: 'details', display: 'value' },
    { key: 'pan_category', label: 'PAN Category', panel: 'details', display: 'value' },
    { key: 'age', label: 'Age', panel: 'details', display: 'value' },
    { key: 'name_match_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'duplicate_pan_flag',
      label: 'Duplicate PAN Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'verification_status',
      label: 'Verification Status',
      panel: 'checks',
      display: 'status',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
  ],
};

const AADHAAR: CategorySpec = {
  key: 'aadhaar',
  label: 'Aadhaar',
  fields: [
    { key: 'name', label: 'Name on Aadhaar', panel: 'details', display: 'value' },
    { key: 'reference_id', label: 'Aadhaar Reference ID', panel: 'details', display: 'value' },
    { key: 'dob', label: 'Date of Birth', panel: 'details', display: 'value' },
    { key: 'gender', label: 'Gender', panel: 'details', display: 'gender' },
    { key: 'address', label: 'Address', panel: 'details', display: 'value' },
    { key: 'pincode', label: 'PIN Code', panel: 'details', display: 'value' },
    { key: 'state', label: 'State', panel: 'details', display: 'value' },
    { key: 'masked_mobile', label: 'Mobile No.', panel: 'details', display: 'value' },
    { key: 'masked_email', label: 'Email Id', panel: 'details', display: 'value' },
    {
      key: 'name_match_score',
      label: 'Name Match Score vs PAN',
      panel: 'checks',
      display: 'score',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
    { key: 'consent_captured_at', label: 'Consent Timestamp', panel: 'checks', display: 'date' },
  ],
};

const FACE: CategorySpec = {
  key: 'face',
  label: 'Face Match',
  fields: [
    {
      key: 'face_matched',
      label: 'Face Matched',
      panel: 'details',
      display: 'flag',
      okWhen: 'yes',
    },
    { key: 'face_match_score', label: 'Face Match Score', panel: 'details', display: 'score' },
    { key: 'confidence', label: 'Confidence', panel: 'details', display: 'score' },
    { key: 'brightness', label: 'Brightness', panel: 'details', display: 'value' },
    { key: 'sharpness', label: 'Sharpness', panel: 'details', display: 'value' },
    {
      key: 'review_needed',
      label: 'Review Needed',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    { key: 'capture_attempts', label: 'Capture Attempts', panel: 'checks', display: 'value' },
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

const LIVENESS: CategorySpec = {
  key: 'liveness',
  label: 'Liveness',
  fields: [
    {
      key: 'liveness_passed',
      label: 'Liveness Passed',
      panel: 'details',
      display: 'flag',
      okWhen: 'yes',
    },
    { key: 'liveness_score', label: 'Liveness Score', panel: 'details', display: 'score' },
    {
      key: 'multiple_faces_detected',
      label: 'Multiple Faces Detected',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'review_needed',
      label: 'Review Needed',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    { key: 'capture_attempts', label: 'Capture Attempts', panel: 'checks', display: 'value' },
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

const BANK: CategorySpec = {
  key: 'bank',
  label: 'Bank Account',
  fields: [
    { key: 'account_number', label: 'Account Number', panel: 'details', display: 'value' },
    {
      key: 'account_holder_name',
      label: 'Account Holder Name',
      panel: 'details',
      display: 'value',
    },
    { key: 'bank_name', label: 'Bank Name', panel: 'details', display: 'value' },
    { key: 'ifsc', label: 'IFSC', panel: 'details', display: 'value' },
    { key: 'account_status', label: 'Account Status', panel: 'details', display: 'value' },
    { key: 'bank_reference', label: 'Bank Reference', panel: 'details', display: 'value' },
    { key: 'name_match_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'name_matched_against',
      label: 'Name Matched Against',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

const GST: CategorySpec = {
  key: 'gst',
  label: 'GST',
  fields: [
    { key: 'gst_identification_number', label: 'GSTIN', panel: 'details', display: 'value' },
    { key: 'gstin_status', label: 'GSTIN Status', panel: 'details', display: 'value' },
    {
      key: 'legal_name_of_business',
      label: 'Legal Name of Business',
      panel: 'details',
      display: 'value',
    },
    { key: 'trade_name', label: 'Trade Name', panel: 'details', display: 'value' },
    {
      key: 'constitution_of_business',
      label: 'Constitution of Business',
      panel: 'details',
      display: 'value',
    },
    { key: 'taxpayer_type', label: 'Taxpayer Type', panel: 'details', display: 'value' },
    {
      key: 'date_of_registration',
      label: 'Date of Registration',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'principal_place_of_business_address',
      label: 'Principal Place of Business',
      panel: 'details',
      display: 'value',
    },
    { key: 'state_jurisdiction', label: 'State Jurisdiction', panel: 'details', display: 'value' },
    {
      key: 'center_jurisdiction',
      label: 'Center Jurisdiction',
      panel: 'details',
      display: 'value',
    },
    { key: 'name_match_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'name_matched_against',
      label: 'Name Matched Against',
      panel: 'checks',
      display: 'value',
    },
    {
      key: 'duplicate_gstin_flag',
      label: 'Duplicate GSTIN Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

// Combined ID Verification — the stored result carries ONLY the document that ran
// (clean keys), so one spec listing every document's fields renders whichever are
// present. Voter/DL detail fields → details panel; matches/flags → checks panel.
const ID_VERIFICATION: CategorySpec = {
  key: 'id_verification',
  label: 'ID Verification',
  fields: [
    // Voter ID
    { key: 'epic_number', label: 'EPIC Number', panel: 'details', display: 'value' },
    { key: 'relative_name', label: "Relative's Name", panel: 'details', display: 'value' },
    { key: 'relation_type', label: 'Relation Type', panel: 'details', display: 'value' },
    { key: 'house_no', label: 'House Number', panel: 'details', display: 'value' },
    {
      key: 'assembly_constituency',
      label: 'Assembly Constituency',
      panel: 'details',
      display: 'value',
    },
    { key: 'polling_station', label: 'Polling Station', panel: 'details', display: 'value' },
    { key: 'district', label: 'District', panel: 'details', display: 'value' },
    { key: 'pin', label: 'PIN Code', panel: 'details', display: 'value' },
    // Passport
    { key: 'passport_number', label: 'Passport Number', panel: 'details', display: 'value' },
    {
      key: 'name_from_passport',
      label: 'Name (from Passport)',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'surname_from_passport',
      label: 'Surname (from Passport)',
      panel: 'details',
      display: 'value',
    },
    { key: 'date_of_issue', label: 'Date of Issue', panel: 'details', display: 'value' },
    {
      key: 'type_of_application',
      label: 'Type of Application',
      panel: 'details',
      display: 'value',
    },
    { key: 'application_date', label: 'Application Date', panel: 'details', display: 'value' },
    // Driving Licence
    { key: 'dl_number', label: 'DL Number', panel: 'details', display: 'value' },
    { key: 'dl_status', label: 'DL Status', panel: 'details', display: 'value' },
    { key: 'issuing_state', label: 'State', panel: 'details', display: 'value' },
    {
      key: 'father_or_spouse_name',
      label: "Father's / Spouse's Name",
      panel: 'details',
      display: 'value',
    },
    { key: 'blood_group', label: 'Blood Group', panel: 'details', display: 'value' },
    { key: 'permanent_address', label: 'Permanent Address', panel: 'details', display: 'value' },
    { key: 'district', label: 'District', panel: 'details', display: 'value' },
    { key: 'vehicle_class', label: 'Vehicle Class', panel: 'details', display: 'value' },
    { key: 'license_issue_date', label: 'License Issue Date', panel: 'details', display: 'value' },
    {
      key: 'non_transport_validity',
      label: 'Non-Transport Validity',
      panel: 'details',
      display: 'value',
    },
    { key: 'transport_validity', label: 'Transport Validity', panel: 'details', display: 'value' },
    { key: 'validity_type', label: 'Validity Type', panel: 'details', display: 'value' },
    // Shared personal
    { key: 'full_name', label: 'Full Name', panel: 'details', display: 'value' },
    { key: 'date_of_birth', label: 'Date of Birth', panel: 'details', display: 'value' },
    { key: 'gender', label: 'Gender', panel: 'details', display: 'gender' },
    { key: 'age', label: 'Age', panel: 'details', display: 'value' },
    { key: 'state', label: 'State', panel: 'details', display: 'value' },
    // Risk signals
    { key: 'name_match', label: 'Name Match', panel: 'checks', display: 'flag', okWhen: 'yes' },
    { key: 'name_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'passport_number_match',
      label: 'Passport Number Match',
      panel: 'checks',
      display: 'flag',
      okWhen: 'yes',
    },
    {
      key: 'date_of_issue_match',
      label: 'Date of Issue Match',
      panel: 'checks',
      display: 'flag',
      okWhen: 'yes',
    },
    { key: 'enforcement_status', label: 'Enforcement Status', panel: 'checks', display: 'value' },
    {
      key: 'initial_issuing_office',
      label: 'Initial Issuing Office',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'last_endorsement_date',
      label: 'Last Endorsement Date',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'last_endorsed_office',
      label: 'Last Endorsed Office',
      panel: 'details',
      display: 'value',
    },
    { key: 'endorsement_reason', label: 'Endorsement Reason', panel: 'details', display: 'value' },
    {
      key: 'hazardous_valid_till',
      label: 'Hazardous Valid Till',
      panel: 'details',
      display: 'value',
    },
    { key: 'hill_valid_till', label: 'Hill Valid Till', panel: 'details', display: 'value' },
    {
      key: 'age_restriction_flag',
      label: 'Age Restriction Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'duplicate_epic_flag',
      label: 'Duplicate EPIC Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'duplicate_dl_flag',
      label: 'Duplicate DL Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    { key: 'last_update', label: 'Electoral Roll Last Updated', panel: 'checks', display: 'value' },
    // Metadata
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

// Utility Bill (Electricity / PNG / LPG). The stored result carries ONLY the
// connection type that ran (normalizer emits clean, un-prefixed keys), so one spec
// covers all three — whichever keys are present render.
const UTILITY_BILL: CategorySpec = {
  key: 'utility_bill',
  label: 'Utility Bill',
  fields: [
    // Electricity
    { key: 'bill_no', label: 'Bill Number', panel: 'details', display: 'value' },
    { key: 'consumer_number', label: 'Consumer Number', panel: 'details', display: 'value' },
    { key: 'bill_issue_date', label: 'Bill Issue Date', panel: 'details', display: 'value' },
    { key: 'bill_due_date', label: 'Bill Due Date', panel: 'details', display: 'value' },
    { key: 'bill_date', label: 'Bill Date', panel: 'details', display: 'value' },
    { key: 'consumer_name', label: 'Consumer Name', panel: 'details', display: 'value' },
    { key: 'address', label: 'Address', panel: 'details', display: 'value' },
    { key: 'mobile_number', label: 'Mobile Number', panel: 'details', display: 'value' },
    { key: 'email_address', label: 'Email Address', panel: 'details', display: 'value' },
    { key: 'bill_amount', label: 'Bill Amount', panel: 'details', display: 'value' },
    { key: 'amount_payable', label: 'Amount Payable', panel: 'details', display: 'value' },
    { key: 'total_amount', label: 'Total Amount', panel: 'details', display: 'value' },
    // PNG (keys not already listed)
    { key: 'due_date', label: 'Due Date', panel: 'details', display: 'value' },
    { key: 'customer_name', label: 'Customer Name', panel: 'details', display: 'value' },
    { key: 'customer_address', label: 'Customer Address', panel: 'details', display: 'value' },
    { key: 'mobile', label: 'Mobile Number', panel: 'details', display: 'value' },
    { key: 'email', label: 'Email Address', panel: 'details', display: 'value' },
    // LPG (keys not already listed)
    { key: 'status', label: 'Status', panel: 'details', display: 'value' },
    { key: 'consumer_no', label: 'Consumer Number', panel: 'details', display: 'value' },
    { key: 'consumer_address', label: 'Consumer Address', panel: 'details', display: 'value' },
    { key: 'pin', label: 'PIN Code', panel: 'details', display: 'value' },
    { key: 'city_town', label: 'City / Town', panel: 'details', display: 'value' },
    { key: 'aadhaar_no', label: 'Aadhaar Number', panel: 'details', display: 'value' },
    { key: 'distributor_name', label: 'Distributor Name', panel: 'details', display: 'value' },
    { key: 'bank_name', label: 'Bank Name', panel: 'details', display: 'value' },
    { key: 'ifsc_code', label: 'IFSC Code', panel: 'details', display: 'value' },
    { key: 'bank_account_no', label: 'Bank Account Number', panel: 'details', display: 'value' },
    { key: 'consumer_email', label: 'Email Address', panel: 'details', display: 'value' },
    { key: 'consumer_contact', label: 'Contact Number', panel: 'details', display: 'value' },
    { key: 'last_booking_date', label: 'Last Booking Date', panel: 'details', display: 'value' },
    {
      key: 'total_refill_consumed',
      label: 'Total Refills Consumed',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'subsidized_refill_consumed',
      label: 'Subsidised Refills Consumed',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'approximate_subsidy_availed',
      label: 'Approx. Subsidy Availed',
      panel: 'details',
      display: 'value',
    },
    { key: 'given_up_subsidy', label: 'Given Up Subsidy', panel: 'details', display: 'value' },
    // Risk signals
    { key: 'name_match_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'bill_overdue_flag',
      label: 'Bill Overdue Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'duplicate_consumer_flag',
      label: 'Duplicate Consumer Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'duplicate_bill_flag',
      label: 'Duplicate Bill Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'due_date_elapsed_flag',
      label: 'Due Date Elapsed Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'dormant_connection_flag',
      label: 'Dormant Connection Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'aadhaar_not_linked_flag',
      label: 'Aadhaar Not Linked Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    // Metadata
    { key: 'verification_status', label: 'Status', panel: 'checks', display: 'status' },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

// Ration Card Verification — Protean ration-card lookup (address / household proof).
const RATION_CARD: CategorySpec = {
  key: 'ration_card',
  label: 'Ration Card',
  fields: [
    { key: 'ration_card_number', label: 'Ration Card Number', panel: 'details', display: 'value' },
    { key: 'state', label: 'State', panel: 'details', display: 'value' },
    { key: 'district', label: 'District', panel: 'details', display: 'value' },
    { key: 'fps_id', label: 'Fair Price Shop ID', panel: 'details', display: 'value' },
    { key: 'scheme_name', label: 'Scheme Name', panel: 'details', display: 'value' },
    { key: 'onorc_allowed', label: 'ONORC Allowed', panel: 'details', display: 'value' },
    { key: 'address', label: 'Address', panel: 'details', display: 'value' },
    { key: 'head_of_family', label: 'Head of Family', panel: 'details', display: 'value' },
    { key: 'member_count', label: 'Number of Members', panel: 'details', display: 'value' },
    { key: 'members', label: 'Members', panel: 'details', display: 'value' },
    {
      key: 'duplicate_uid_status',
      label: 'Duplicate UID Status',
      panel: 'checks',
      display: 'value',
    },
    { key: 'name_match_score', label: 'Name Match Score', panel: 'checks', display: 'score' },
    {
      key: 'name_matched_against',
      label: 'Name Matched Against',
      panel: 'checks',
      display: 'value',
    },
    {
      key: 'duplicate_ration_card_flag',
      label: 'Duplicate Ration Card Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'verification_status',
      label: 'Verification Status',
      panel: 'checks',
      display: 'status',
    },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

const MSME: CategorySpec = {
  key: 'msme',
  label: 'MSME (Udyam)',
  fields: [
    // Details — identity / classification / profile / financial / address.
    {
      key: 'udyam_registration_no',
      label: 'Udyam Registration Number',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'date_of_registration',
      label: 'Date of Registration',
      panel: 'details',
      display: 'value',
    },
    { key: 'type_of_enterprise', label: 'Type of Enterprise', panel: 'details', display: 'value' },
    {
      key: 'enterprise_type_history',
      label: 'Enterprise Type History',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'industry_classification',
      label: 'Industry Classification',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'interested_in_treds',
      label: 'Interested in TReDS',
      panel: 'details',
      display: 'value',
    },
    { key: 'interested_in_gem', label: 'Interested in GeM', panel: 'details', display: 'value' },
    { key: 'business_name', label: 'Business Name', panel: 'details', display: 'value' },
    { key: 'major_activity', label: 'Major Activity', panel: 'details', display: 'value' },
    { key: 'organization_type', label: 'Organization Type', panel: 'details', display: 'value' },
    { key: 'owner_name', label: 'Owner Name', panel: 'details', display: 'value' },
    { key: 'pan', label: 'PAN', panel: 'details', display: 'value' },
    { key: 'gender', label: 'Gender', panel: 'details', display: 'gender' },
    { key: 'social_category', label: 'Social Category', panel: 'details', display: 'value' },
    { key: 'specially_abled', label: 'Specially Abled', panel: 'details', display: 'value' },
    {
      key: 'date_of_incorporation',
      label: 'Date of Incorporation',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'date_of_commencement',
      label: 'Date of Commencement',
      panel: 'details',
      display: 'value',
    },
    {
      key: 'previous_itr_status',
      label: 'Previous ITR Status',
      panel: 'details',
      display: 'value',
    },
    { key: 'itr_type', label: 'ITR Type', panel: 'details', display: 'value' },
    { key: 'gstn_status', label: 'GSTIN Status', panel: 'details', display: 'value' },
    { key: 'mobile', label: 'Mobile Number', panel: 'details', display: 'value' },
    { key: 'email_id', label: 'Email Address', panel: 'details', display: 'value' },
    { key: 'bank_name', label: 'Bank Name', panel: 'details', display: 'value' },
    { key: 'ifsc_code', label: 'IFSC Code', panel: 'details', display: 'value' },
    { key: 'bank_account_no', label: 'Bank Account Number', panel: 'details', display: 'value' },
    { key: 'employment_male', label: 'Male Employees', panel: 'details', display: 'value' },
    { key: 'employment_female', label: 'Female Employees', panel: 'details', display: 'value' },
    { key: 'employment_others', label: 'Other Employees', panel: 'details', display: 'value' },
    { key: 'employment_total', label: 'Total Employees', panel: 'details', display: 'value' },
    { key: 'financial_year', label: 'Financial Year', panel: 'details', display: 'value' },
    { key: 'wdv_pme', label: 'WDV of Plant & Machinery', panel: 'details', display: 'money' },
    { key: 'exclusion', label: 'Exclusion', panel: 'details', display: 'money' },
    {
      key: 'net_investment_in_pme',
      label: 'Net Investment in P&M',
      panel: 'details',
      display: 'money',
    },
    { key: 'total_turnover', label: 'Total Turnover', panel: 'details', display: 'money' },
    { key: 'export_turnover', label: 'Export Turnover', panel: 'details', display: 'money' },
    { key: 'net_turnover', label: 'Net Turnover', panel: 'details', display: 'money' },
    { key: 'official_address', label: 'Official Address', panel: 'details', display: 'value' },
    { key: 'branch_details', label: 'Branch Details', panel: 'details', display: 'value' },
    {
      key: 'pdf_document',
      label: 'Registration Certificate (PDF)',
      panel: 'details',
      display: 'document',
    },
    // Checks — risk signals + metadata.
    {
      key: 'enterprise_type_mismatch_flag',
      label: 'Enterprise Type Mismatch Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'pan_match_flag',
      label: 'PAN Match Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'yes',
    },
    {
      key: 'turnover_threshold_flag',
      label: 'Turnover Threshold Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'duplicate_udyam_flag',
      label: 'Duplicate Udyam Flag',
      panel: 'checks',
      display: 'flag',
      okWhen: 'no',
    },
    {
      key: 'verification_status',
      label: 'Verification Status',
      panel: 'checks',
      display: 'status',
    },
    {
      key: 'verification_attempts',
      label: 'Verification Attempts',
      panel: 'checks',
      display: 'value',
    },
    { key: 'verified_at', label: 'Verified At', panel: 'checks', display: 'date' },
    { key: 'method_used', label: 'Method Used', panel: 'checks', display: 'value' },
  ],
};

// Resolve a step's category spec by its stepType (falls back to a substring match
// on the display name so unusual step-type strings still resolve).
// ── Document steps ───────────────────────────────────────────────────────────
// Not verifications: nothing is checked against a provider, so there is no risk
// signal to show. What matters is WHAT was produced and WHERE it went.

const DOCUMENT_TEMPLATE: CategorySpec = {
  key: 'document_template',
  label: 'Document',
  fields: [
    {
      key: 'accepted',
      label: 'Document reviewed',
      panel: 'details',
      display: 'flag',
      okWhen: 'yes',
    },
    { key: 'accepted_at', label: 'Reviewed at', panel: 'checks', display: 'date' },
  ],
};

const ESIGN_TEMPLATE: CategorySpec = {
  key: 'esign_template',
  label: 'Signature request',
  // Dotted paths: CreateSessionEsignCommandHandler groups its result under `esign`.
  fields: [
    { key: 'esign.document_id', label: 'SignCare document', panel: 'details', display: 'value' },
    { key: 'esign.emailed_signers', label: 'Sent to', panel: 'details', display: 'list' },
    { key: 'esign.signed_at', label: 'Signed at', panel: 'details', display: 'date' },
    { key: 'esign.sent_at', label: 'Sent for signature', panel: 'checks', display: 'date' },
    { key: 'esign.signed_count', label: 'Signatures collected', panel: 'checks', display: 'value' },
    { key: 'esign.signer_count', label: 'Signers', panel: 'checks', display: 'value' },
    { key: 'esign.status', label: 'Signature status', panel: 'checks', display: 'status' },
  ],
};

export function categoryForStep(step: SessionStepDto): CategorySpec | null {
  const t = `${step.stepType} ${step.displayName}`.toLowerCase();
  // Document steps first: "eSign Document Template" contains "document template",
  // so the more specific type has to win, and neither must fall through to a
  // verification category.
  if (t.includes('esign_template') || t.includes('esign document template')) return ESIGN_TEMPLATE;
  if (t.includes('document_template') || t.includes('document template')) return DOCUMENT_TEMPLATE;
  if (t.includes('pan')) return PAN;
  if (t.includes('aadhaar') || t.includes('aadhar')) return AADHAAR;
  if (t.includes('bank')) return BANK;
  if (t.includes('gst')) return GST;
  if (t.includes('id_verif') || t.includes('id verification')) return ID_VERIFICATION;
  if (t.includes('utility_bill') || t.includes('utility bill')) return UTILITY_BILL;
  if (t.includes('ration')) return RATION_CARD;
  if (t.includes('msme') || t.includes('udyam')) return MSME;
  if (t.includes('liveness')) return LIVENESS;
  if (t.includes('face')) return FACE;
  return null;
}

// Parse a step's resultJson into a plain object (null on absent/malformed).
export function parseResult(resultJson: string | null): Record<string, unknown> | null {
  if (!resultJson) return null;
  try {
    const p = JSON.parse(resultJson);
    return p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export interface ResolvedField {
  field: ReturnField;
  raw: unknown;
  text: string;
}

// The fields of one panel that are actually present in the result, formatted.
export function resolveFields(
  spec: CategorySpec,
  result: Record<string, unknown> | null,
  panel: 'details' | 'checks',
): ResolvedField[] {
  if (!result) return [];
  const out: ResolvedField[] = [];
  for (const field of spec.fields) {
    if (field.panel !== panel) continue;
    const raw = readPath(result, field.key);
    if (raw === null || raw === undefined || raw === '') continue;
    if (Array.isArray(raw) && raw.length === 0) continue;
    out.push({ field, raw, text: formatValue(field, raw) });
  }
  return out;
}

/** Reads a flat key or a dotted path; undefined when any segment is missing. */
function readPath(result: Record<string, unknown>, key: string): unknown {
  if (!key.includes('.')) return result[key];

  let cursor: unknown = result;
  for (const segment of key.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

// Voter ID relation-type codes returned by the gateway (rlnType). Expanded for
// display; any unknown value is shown verbatim as it comes from SignCare.
const RELATION_TYPE_LABELS: Record<string, string> = {
  F: 'Father',
  M: 'Mother',
  H: 'Husband',
  W: 'Wife',
};

export function formatValue(field: ReturnField, value: unknown): string {
  if (value === null || value === undefined) return '';
  // Voter relation type comes as a single-letter code (F/M/H/W) — expand it, but
  // fall back to the raw value for anything unrecognised.
  if (field.key === 'relation_type') {
    const raw = String(value).trim();
    return RELATION_TYPE_LABELS[raw.toUpperCase()] ?? raw;
  }
  switch (field.display) {
    case 'score': {
      const n = Number(value);
      return Number.isNaN(n) ? String(value) : `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
    }
    case 'money': {
      const n = Number(value);
      return Number.isNaN(n) ? String(value) : `₹${n.toLocaleString('en-IN')}`;
    }
    case 'document':
      // Offloaded file — the raw value is a storage key/path, not for display.
      return 'Available';
    case 'date': {
      return formatResultDateTime(value);
    }
    case 'gender':
      return formatGender(value);
    case 'list':
      return Array.isArray(value) ? value.map(String).join(', ') : String(value);
    case 'flag': {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      // Some gateways return yes/no as strings — normalise the casing.
      const s = String(value).trim().toLowerCase();
      if (s === 'yes' || s === 'true') return 'Yes';
      if (s === 'no' || s === 'false') return 'No';
      return String(value);
    }
    default:
      return String(value);
  }
}

// For `flag` fields — is the value the "good" one (green check) or not?
export function flagIsOk(field: ReturnField, value: unknown): boolean {
  const truthy = value === true || value === 'true' || value === 'Yes' || value === 'yes';
  return field.okWhen === 'yes' ? truthy : !truthy;
}

/**
 * WHY a step did not pass — the one-line reason the rule engine recorded.
 *
 * The backend writes this onto the result as `verification_reason`
 * (SessionRuleEngine.InjectReason) whenever the outcome is not a pass: a duplicate
 * identity, an invalid document, a failed name match, a firing risk rule.
 *
 * Deliberately NOT `step.riskOutcome`. That field holds the verdict WORD the rule engine
 * settled on — literally "Approve", "Review" or "Reject" — so showing it as a reason
 * produced entries like "Approve — PAN Verification", which reads as a warning on a step
 * that passed cleanly.
 *
 * Not catalogued as a `ReturnField` either: it belongs to the step's outcome, not to the
 * verification data, and it is rendered as a banner rather than as a label/value row.
 *
 * Returns null for a passed step even if a reason is present — an earlier attempt's
 * reason is not why the run stands where it does now.
 */
export function stepReason(step: SessionStepDto): string | null {
  if (step.status === 'Passed') return null;
  const value = parseResult(step.resultJson)?.verification_reason;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
